import { Collection } from 'firestorter';
import firebase from 'firebase/app';
import callRemote from '../utils/remote';
import Collections from '../utils/collections';
import BaseDocument from './BaseDocument';

export const ReleaseNoteStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
};

const RELEASE_NOTES_ACTION_PATH = 'slackInteractHandler/action';
const MAX_RELEASE_NOTES = 100;

class ReleaseNote extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.RELEASE_NOTE}/${id}`, opts);
  }

  get appVersion() {
    return this.data.appVersion;
  }

  get features() {
    return this.data.features || [];
  }

  get photoUrl() {
    return this.data.photoUrl || null;
  }

  get status() {
    return this.data.status;
  }

  get generatedAt() {
    return this.data.generatedAt;
  }

  get approvedAt() {
    return this.data.approvedAt || null;
  }

  get rejectionReason() {
    return this.data.rejectionReason || null;
  }

  get sendNotification() {
    return this.data.sendNotification ?? false;
  }

  get iosPublished() {
    return !!this.data.iosPublished;
  }

  get androidPublished() {
    return !!this.data.androidPublished;
  }

  async callReleaseNoteAction(payload) {
    const { currentUser } = firebase.auth();
    const idToken = await currentUser.getIdToken();
    const actorName = currentUser.displayName || currentUser.email;
    const response = await callRemote(idToken, RELEASE_NOTES_ACTION_PATH, {
      docId: this.id,
      actorName,
      ...payload,
    });
    if (!response.ok) {
      throw new Error(`Action failed: ${response.status}`);
    }
  }

  /**
   * Approves the release note with the given content.
   *
   * This is a two-phase write: content (features, photoUrl) is persisted first,
   * then the action call transitions the status to APPROVED.
   * If the action call fails, the document retains the updated content but stays PENDING — retry is safe.
   *
   */
  async approve(features, photoUrl, sendNotification) {
    await this.updateFields({ features, photoUrl });
    await this.callReleaseNoteAction({
      action: 'approve',
      sendNotification: sendNotification !== undefined ? sendNotification : this.sendNotification,
    });
  }

  async reject(reason) {
    await this.callReleaseNoteAction({
      action: 'reject',
      reason: reason || null,
    });
  }

  static getAllNotes() {
    return new Collection(Collections.RELEASE_NOTE, {
      createDocument: (src, opts) => new ReleaseNote(src.id, opts),
      query: (ref) => ref.orderBy('generatedAt', 'desc').limit(MAX_RELEASE_NOTES),
    });
  }
}

export default ReleaseNote;
